import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { actionsApi } from '../api/actions'
import type { ActionSharedState, ActionStatus, Priority } from '../lib/types'
import { ACTION_REASONS_BY_STATUS, ACTION_STATUS_LABEL, ACTION_STATUS_OPTIONS } from '../lib/format'
import { containsDocumentLink, DOC_LINK_IN_NOTE_MESSAGE } from '../lib/contextLinks'
import { PriorityBadge } from './PriorityBadge'
import { ActionStateBadge } from './ActionStateView'
import { ReasonChips, reasonFitsStatus, reasonMissing } from './ReasonChips'
import { Spinner } from './Spinner'

export interface QuickEditAction extends ActionSharedState {
  id: string
  type: 'optimization' | 'derived'
  description: string
  priority: Priority
}

/** Popup de edição rápida do status/nota de uma ação (aberta). */
export function ActionQuickEditModal({
  action,
  onClose,
}: {
  action: QuickEditAction
  onClose: () => void
}) {
  const qc = useQueryClient()
  // Estado ÚNICO da ação: salvar sobrescreve status e comentário para todos.
  const [status, setStatus] = useState<ActionStatus>(action.state.status)
  const [note, setNote] = useState(action.state.note ?? '')
  const [reason, setReason] = useState<string | null>(action.state.reason ?? null)
  const [reviewAt, setReviewAt] = useState(action.state.reviewAt ?? '')
  const [noteError, setNoteError] = useState<string | null>(null)

  const changeStatus = (s: ActionStatus) => {
    setStatus(s)
    if (!reasonFitsStatus(s, reason)) setReason(null)
  }

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        status,
        note: note.trim() ? note : null,
        reason: reasonFitsStatus(status, reason) ? reason : null,
        reviewAt: status === 'review' ? reviewAt : null,
      }
      return action.type === 'optimization'
        ? actionsApi.logOptimization(action.id, body)
        : actionsApi.logDerived(action.id, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['open-actions'] })
      qc.invalidateQueries({ queryKey: ['planning'] })
      onClose()
    },
  })

  const handleSave = () => {
    if (containsDocumentLink(note)) {
      setNoteError(DOC_LINK_IN_NOTE_MESSAGE)
      return
    }
    setNoteError(null)
    mutation.mutate()
  }

  // Portal para o body: evita que um ancestral com `transform` (ex.: animate-fade-up
  // do <main>) vire o bloco de contenção do `fixed` e "esconda" o modal.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <PriorityBadge priority={action.priority} />
            <h3 className="text-sm font-semibold text-slate-900">Atualizar ação</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fechar">
            ✕
          </button>
        </div>

        <p className="mb-2 text-sm text-slate-700">{action.description}</p>
        {action.state.status !== 'pending' && (
          <p className="mb-3">
            <ActionStateBadge state={action.state} />
          </p>
        )}

        <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
          {ACTION_STATUS_OPTIONS.map((opt) => (
            <label key={opt} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
              <input
                type="radio"
                name={`qe-status-${action.id}`}
                checked={status === opt}
                onChange={() => changeStatus(opt)}
                className="text-brand-600 focus:ring-brand-500"
              />
              {ACTION_STATUS_LABEL[opt]}
            </label>
          ))}
        </div>

        {ACTION_REASONS_BY_STATUS[status] && (
          <div className="-mt-1 mb-3">
            <ReasonChips status={status} value={reason} onChange={setReason} />
          </div>
        )}

        {status === 'review' && (
          <label className="mb-3 flex items-center gap-2 text-sm text-slate-600">
            Rever em:
            <input
              type="date"
              value={reviewAt}
              onChange={(e) => setReviewAt(e.target.value)}
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
        )}

        <textarea
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            if (noteError) setNoteError(null)
          }}
          rows={3}
          placeholder="Comentário da ação (visível para todos)"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
        {noteError && <p className="mt-1 text-xs text-red-600">{noteError}</p>}

        <div className="mt-3 flex items-center justify-end gap-2">
          {mutation.isError && <span className="mr-auto text-xs text-red-600">Erro ao salvar.</span>}
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={mutation.isPending || (status === 'review' && !reviewAt) || reasonMissing(status, reason)}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending && <Spinner className="h-4 w-4 text-white" />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
